import { mapGetters } from 'vuex'
import { DateTime } from 'luxon'

export default {
  data() {
    return {
      global: {
        request: {
          me: {
            resource: 'ag.practitioner.me',
            loading: false
          },
          lookups: {
            resource: 'ag.practitioner.me.lookup',
            loading: false
          }
        }
      }
    }
  },
  methods: {
    idsAreEqual(id1, id2, verbose) {
      if (id1 === null || id2 === null) { return false }
      let result = parseInt(id1) === parseInt(id2)
      if (verbose) {
        console.log(id1, id2, result)
      }
      return result
    },
    getObjectPath(object, path, defaultResponse) {
      if (object) {
        let bits = path.split('.')
        for (let key of bits) {
          try {
            if (Object.keys(object).indexOf(key) !== -1) {
              object = object[key]
            } else {
              return defaultResponse
            }
          } catch (e) {
            if (e instanceof TypeError) {
              return defaultResponse
            }
            throw e
          }
        }
        return object
      }
      return defaultResponse
    },
    safelyRemoveKey(obj, key) {
      if (key in obj) {
        delete obj.key;
      }
    },
    getPractitionerId(p) {
      if (p) {
        if (p.id) { return p.id }
        if (p.user && p.user.id) { return p.user.id }
        if (p.practitioner && p.practitioner.id) { return p.practitioner.id }
      }
      return null
    },
    dateformat(dt, preset, format) {
      if (typeof (dt) === 'string') {
        dt = DateTime.fromISO(dt)
      }
      if (!dt) { return dt }
      if (preset) {
        return dt.toLocaleString(DateTime[preset])
      }
      if (format) {
        return dt.toFormat(format)
      }
      return dt.toLocaleString(DateTime.DATE_MED_WITH_WEEKDAY)
    },
    formatCurrency(currency, number, locale = 'en-ZA') {
      if (!currency) { currency = this.currency }
      if (currency) {
        let formatter = new Intl.NumberFormat(locale, {
          style: 'currency',
          currency: currency
        })
        return formatter.format(number)
      } else {
        return number
      }
    },
    clone(dta) {
      return JSON.parse(JSON.stringify(dta))
    },
    init() {
      this.getMe()
      this.lookup()
    },
    getMe() {
      this.$request('get', this.global.request.me)
    },
    lookup() {
      this.$request('get', this.global.request.lookups)
    },
    copyText(text, callback) {
      if (!callback) {
        callback = () => {
          window.alert(`Copied ${text} to clipboard`)
        }
      }
      if (navigator && navigator.clipboard) {
        navigator.clipboard.writeText(text).then(callback)
      } else {
        window.alert("Copying to clipboard is not supported by this browser")
      }
    },
    getProfilePicture(p) {
      if (p) {
        if (p.profile_picture) { return p.profile_picture }
        if (p.profile && p.profile.profile_picture) {
          return p.profile.profile_picture
        }
      }
      return null
    },
    getInitials(fullname, numLetters) {
      if (!numLetters) { numLetters = 2 }
      if (fullname) {
        let items = fullname.split(' ')
        if (items && items.length > 0) {
          return items.slice(0, numLetters).map((word) => { return word[0] }).join('')
        }
      }
      return null
    },
    logoPicture(item) {
      let img = img = this.getObjectPath(item, 'logo_picture')
      if (!img) { img = this.getObjectPath(item, 'settings.logo_picture') }
      if (!img) { img = this.getObjectPath(item, 'profile.logo_picture') }
      return img
    },
    getIntegration(title) {
      let integrations = this.getObjectPath(this, 'lookups.integrations', [])
      return integrations.find((integration) => {
        return integration.title === title
      })
    },
    getWidgetURL(type, id) {
      if (type === 'practitioner') {
        return `https://book.appt.guru/?p=${id}`
      } else {
        return `https://book.appt.guru/?s=${id}`
      }
    },
    getWebsiteURL(type, obj) {
      if (obj) {
        if (type === 'practitioner') {
          return `https://${obj.sub_domain}.appt.guru`
        }
        if (type === 'space') {
          return `https://${obj.slug}.appt.guru`
        }
      }
    },
    getCalendarFeedURL(feedId) {
      let base = 'https://appt.guru/calendar/ical/practitioner'
      return `${base}/${feedId}/`
    }
  },
  watch: {
    isLoggedIn() {
      // see: mixins/global.js
      this.init()
    },
    globalMeRequestStatus() {
      if (this.global.request.me.status === 200) {
        this.$store.commit('identity/setMe', this.global.request.me.result)
      }
    },
    globalLookupRequestStatus() {
      if (this.global.request.lookups.status === 200) {
        this.$store.commit('identity/setLookups', this.global.request.lookups.result)
      }
    }
  },
  computed: {
    ...mapGetters('identity', [
      'me',
      'businesses',
      'lookups',
      'spaces',
      'spacesList',
      'spacesIOwn',
      'getSpaceById'
    ]),
    ...mapGetters('auth', [
      'auth',
    ]),
    tld() { return 'appt.guru' },
    calculatedBusinessId() {
      if (this.businesses && this.businesses.length > 0) {
        return this.businesses[0].id
      }
      return null
    },
    isActorDependent() {
      let isDep = true
      this.spacesList.forEach((space) => {
        if (space.role === 'Owner') { isDep = false }
      })
      return isDep
    },
    spaceData() {
      if (this.spaceId) {
        return this.getSpaceById(this.spaceId)
      }
      return {}
    },
    spaceName() {
      return this.getObjectPath(this.spaceData, 'title', '')
    },
    currency() {
      let currency = this.getObjectPath(this.spaceData, 'settings.currency')
      if (!currency) {
        currency = this.getObjectPath(this.me, 'currency', 'ZAR')
      }
      return currency
    },
    globalMeRequestStatus() {
      return this.getObjectPath(this.global, 'request.me.status')
    },
    globalLookupRequestStatus() {
      return this.getObjectPath(this.global, 'request.lookups.status')
    },
    globalInitLoading() {
      return (this.global.request.me.loading === true || this.global.request.lookups.loading === true)
    },
    topLevelPages() {
      return [
        { to: '/settings/spaces/', title: 'Spaces' },
        { to: '/settings/team/', title: 'Team' },
        { to: '/settings/me/profile/', title: 'Profile' },
        { to: '/brand/', title: 'Brand' },
      ]
    }
  }
}
